import { useContext, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import mixpanel from "mixpanel-browser";

// eslint-disable-next-line import/default, import/no-named-as-default-member
import authenticate from "../../utils/authenticate";

import FullPageLoader from "../../components/full-page-loader";
import { AppContext } from "../../context/AppContext";
import TransferScreen from "../../widgets/transfer-screen";
import sendError from "../../utils/sendError";
import {
  BRIDGE_DEVELOPMENT,
  BridgeEnv,
  ERROR_CODE_400,
  TRANSFER_FLOW_IN,
  TRANSFER_FLOW_OUT,
  TRANSFER_FLOW_STATUS,
  TransferFlow,
} from "../../typings";
import sendOpen from "../../utils/sendOpen";
import parseUrl from "../../utils/parseUrl";
import TransferDetails, { TransferStatusTypes } from "../../class/transferDetails";
import { TransferContext } from "../../context/TransferContext";
import UserDetails from "../../class/userDetails";
import { TransferIntent } from "../../class/transferIntent";
import getAPI from "../../utils/getAPI";
import { sampleTransferInstructions } from "../../utils/__test__/test-data";
import { sampleTransferIntentInResponseIra } from "../../api/mockAPIResponse";
import { setSentryContext } from "../../lib/sentry";

import ErrorPane from "../../widgets/general/error-pane";
import Analytics from "../../utils/productAnalytics";

const Home = () => {
  const [isAuth, setIsAuth] = useState<boolean>(false);
  const {
    setAppEnv,
    setPartnerConfig,
    setTransferFlow,
    isAppLoading,
    setIsAppLoading,
    setTransferIntentId,
    setAccessToken,
    setTransferId,
    setClientId,
    setIsWebview,
  } = useContext(AppContext);
  const {
    updateTransferDetails,
    setTransferIntentDetails,
    updateUserDetails,
    setTransferInstructions,
    setUserId,
    setCanStart,
  } = useContext(TransferContext);

  /** 
   * Fetch 
   *  transferFlow
   *  userDetails from transferIntentDetails
   *  transferDetails from transferIntentDetails and see if there is any inprogress transfer
   *  userDetails from internal api if there is in progress transfer

  **/
  const fetchAllDetails = async ({
    env = BRIDGE_DEVELOPMENT,
    transferIntentDetails,
  }: {
    env: BridgeEnv;
    transferIntentDetails: any;
  }) => {
    let transferDetails;
    let userDetails = new UserDetails();
    let transferFlow = TRANSFER_FLOW_IN;
    if (transferIntentDetails) {
      transferFlow = transferIntentDetails.mode === "TRANSFER_OUT" ? TRANSFER_FLOW_OUT : TRANSFER_FLOW_IN;
      userDetails = UserDetails.fromTransferIntent(transferIntentDetails.user);
      // transfer intent reference flow
      try {
        const transferDetailsRes = await getAPI(env)({
          method: "GET",
          url: `/api/userIdentity/transferIntent/${transferIntentDetails.transferIntentId}/transfer`,
        });
        // update transfer details
        transferDetails = transferDetailsRes.data.data;
        updateTransferDetails(TransferDetails.fromAPI(transferDetails));
        setTransferId(transferDetails.transferId);
      } catch (e) {
        // do nothing
      }
    }
    // fetch user details
    const userId = transferDetails?.userId;
    if (userId) {
      setUserId(userId);
    } else if (transferIntentDetails.user) {
      updateUserDetails(UserDetails.fromTransferIntent(transferIntentDetails.user));
    }
    if (
      transferDetails &&
      transferDetails?.transferStatus !== TransferStatusTypes.inProgress &&
      userId &&
      userId !== "undefined"
    ) {
      try {
        const transferInstructionRes = await getAPI(env)({
          method: "GET",
          url: `/api/userIdentity/${userId}/transfer/transferInstruction`,
        });
        const transferInstructions = transferInstructionRes?.data?.data || [];

        transferFlow = TRANSFER_FLOW_STATUS;

        setTransferInstructions(transferInstructions);
      } catch (e) {
        // do nothing
      }
    }
    setCanStart(true);
    setTransferFlow(transferFlow as TransferFlow);
  };

  useEffect(() => {
    const init = async () => {
      sendOpen("init");
      setIsAppLoading(true);
      const parentUrl = document.location.ancestorOrigins
        ? document.location.ancestorOrigins[0]
        : document.referrer
          ? document.referrer
          : undefined;
      const nodeEnv = process.env.NODE_ENV || "development";

      const { accessToken, clientId, transferIntentId, userId, environment, webview } = parseUrl(window.location.href);
      if (nodeEnv !== "test" && !parentUrl && !webview) return;
      sendOpen("loading");
      const {
        ready,
        env = undefined,
        errorCode = undefined,
        errorMessage = undefined,
        transferFlow = undefined,
        clientName = undefined,
        data = {},
      } = await authenticate({
        accessToken,
        clientId,
        transferIntentId,
        userId,
        environment,
      });
      if (errorCode && errorMessage) {
        sendError({ errorCode, errorMessage }, webview);
        sendOpen("error");
        return;
      }

      setIsWebview(webview);

      setAppEnv(env);
      // default mixpanelId for dev
      let mixpanelId = "bd5d25f14e000177792ba847934b1685";
      if (process.env.REACT_APP_ENV === "production" && environment !== "development") {
        // this is the production project id
        mixpanelId = "4923764bd4dfd40af65dc365fd82f44b";
      }
      mixpanel.init(mixpanelId, {
        debug: false,
        track_pageview: false,
        persistence: "localStorage",
        record_sessions_percent: 100,
        record_heatmap_data: true,
        autocapture: {
          click: true,
          capture_text_content: true,
          capture_extra_attrs: ["data-button-title", "data-name", "data-current-url", "name"],
        },
        record_mask_text_selector: "",
      });
      // TODO: Put this back after testing
      // if (env === "BRIDGE_DEMO" || env === "BRIDGE_DEVELOPMENT") {
      //   mixpanel.opt_out_tracking();
      // }

      if (clientName) {
        setPartnerConfig(clientName);
        Analytics.setPartnerName(clientName);
      }

      if (ready) {
        const {
          transferIntentDetails = undefined,
          transferInstructionDetails = [], // TODO: STOP HERE: FETCH USER DETAILS DIRECTLY if transfer instruction details are not empty
        } = data;
        if (transferIntentDetails) {
          setTransferIntentDetails(new TransferIntent(transferIntentDetails));
          Analytics.setUserProfiles({
            userName: `${transferIntentDetails.user?.firstName || ""} ${transferIntentDetails.user?.lastName || ""}`,
            userEmail: transferIntentDetails.user?.email,
            partnerName: clientName || "",
            partnerId: clientId,
            transferFlow: TRANSFER_FLOW_IN,
          });
        }
        if (transferInstructionDetails.length > 0) {
          setTransferInstructions(transferInstructionDetails);
        }
        if (!transferIntentDetails && transferInstructionDetails.length === 0) {
          sendError({ errorCode: ERROR_CODE_400, errorMessage: "Transfer Intent Id or UserId not found" }, webview);
          sendOpen("error");
          return;
        }
        if (clientId.includes("bridge")) {
          mixpanel.opt_out_tracking();
          updateUserDetails(UserDetails.fromTransferIntent(sampleTransferIntentInResponseIra.user));
          if (transferFlow === TRANSFER_FLOW_STATUS) {
            setTransferInstructions(sampleTransferInstructions);
          }
          if (transferFlow) {
            setTransferFlow(transferFlow);
          } else {
            setTransferFlow(TRANSFER_FLOW_IN);
          }
          setCanStart(true);
        } else {
          setSentryContext({ transferIntentId, userId, clientId });
          let updatedTransferIntentId = "";
          if (transferIntentDetails) {
            updatedTransferIntentId = transferIntentDetails.transferIntentId;
          }
          if (!updatedTransferIntentId && transferIntentId && transferIntentId !== "undefined") {
            updatedTransferIntentId = transferIntentId;
          }
          setTransferIntentId(updatedTransferIntentId);
          if (transferInstructionDetails.length === 0) {
            await fetchAllDetails({
              env: env || BRIDGE_DEVELOPMENT,
              transferIntentDetails,
            });
          } else {
            const userResponse = await getAPI(env)({
              method: "GET",
              url: `/api/userIdentity/${userId}/userDetails`,
            });
            setUserId(userId);
            const userResponseData = userResponse.data.data.userDetails;
            updateUserDetails(
              new UserDetails({
                ...userResponseData,
              }),
            );

            setTransferFlow(TRANSFER_FLOW_STATUS);
          }
        }
        setAccessToken(accessToken);
        setClientId(clientId);

        setCanStart(true);
        setIsAppLoading(false);
        setIsAuth(ready);
        sendOpen("success");
      }
    };
    init();
  }, []);

  if (isAppLoading) return <FullPageLoader />;
  return isAuth ? (
    <ErrorBoundary FallbackComponent={ErrorPane}>
      <TransferScreen />
    </ErrorBoundary>
  ) : (
    <FullPageLoader />
  );
};

export default Home;
